// Angular
import { Component, Input, OnChanges, OnInit } from '@angular/core';

//Afidium
import { ITraveller, IGetBookingsAirResponse, ITicket, IXId, ISeatAssignment, IRule } from 'libraries/service/resources/booking/interfaces/IGetBookings';
import { SectorAirServicesService, IServiceByCategoryGettersInput }from 'libraries/component/booking-information/components/sectors/components/shared/services/sector-air-services/sector-air-services.service';
import { ILabels } from 'libraries/interfaces/ILabels';
import { Yii } from 'libraries/interfaces/Yii';
import { IGetEnumsService } from 'libraries/service/booking/enums/IGetEnums';
import { LuggageType } from 'libraries/service/resources/booking/interfaces/get-bookings.enum';

//libs
import { faTicketAlt, faMapMarker, faCoffee, IconDefinition, faCreditCard } from "@fortawesome/free-solid-svg-icons";

interface ITravellerDetail{
    luggage: string;
    luggageRule: IRule[];
    mealplan: string;
    payableService: string;
    seat: string;
    seatNumber: string;
    ticket: string;
    ticketNumber: string;
}

const LUGGAGE_TYPE_LABELS: { [key: string]: string } = {
    [LuggageType.hold]: Yii.t('wui-shared-js', 'Soute'),
    [LuggageType.cabin]: Yii.t('wui-shared-js', 'Cabine'),
};

@Component({
    selector: 'sector-air-details-traveller',
    templateUrl: 'sector-air-details-traveller.component.html',
    styles: [`
        .traveller-container{
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
        }
        .traveller-column{
            margin-top: .75rem;
            margin-right: .75rem;
            min-width: 6rem;
        }
        .traveller-identity{
            flex: 1.2;
        }
        .ticket-seat{
            flex: 1;
        }
        .luggage-mealplan-services{
            flex: 1.1;
        }
        .undefined-data{
            color: #707070;
        }
        loader.traveller-column{
            color: #666;
            flex-grow: .6;
        }
        i.fa{
            width: 1.25rem;
            text-align: center;
        }
    `]
})

export class SectorAirDetailsTravellerComponent implements OnInit, OnChanges  {
    @Input()
    public travellerData: ITraveller;

    @Input()
    public bookingData: IGetBookingsAirResponse;

    @Input()
    public sectorXId: string;

    @Input()
    public serviceEnum: IGetEnumsService[];

    public labels: ILabels = {
        luggageUndefined: Yii.t('wui-shared-js', "Bagage non défini"),
        enumOnLoad: Yii.t('wui-shared-js', "Chargement de la configuration des services"),
    };

    public isEnumLoading: boolean;

    public travellerDetail: ITravellerDetail = {
        luggage: undefined,
        luggageRule: undefined,
        mealplan: undefined,
        payableService: undefined,
        seat: undefined,
        seatNumber: undefined,
        ticket: undefined,
        ticketNumber: undefined,
    };

    public iconTicket: IconDefinition = faTicketAlt;
    public iconMapMarker: IconDefinition = faMapMarker;
    public iconCoffee: IconDefinition = faCoffee;
    public iconCreditCard: IconDefinition = faCreditCard;

    constructor(
        private readonly sectorAirServicesService: SectorAirServicesService,
    ){}

    public ngOnInit(): void {
        this.isEnumLoading = !Boolean(this.serviceEnum);
        this.init();
    }

    public ngOnChanges(): void {
        if (this.travellerData && this.serviceEnum){
            this.init();
        }
    }

    private init(): void {
        const getterParameters: IServiceByCategoryGettersInput = {
            ssr: this.bookingData.ssr,
            travellerXId: this.travellerData.xId,
            sectorXId: this.sectorXId,
            serviceEnum: this.serviceEnum
        };
        this.travellerDetail = {
            luggage: this.bookingData.ssr?.length ? this.sectorAirServicesService.getLuggageServices(getterParameters): undefined,
            luggageRule: this.bookingData.rule?.length ? getLuggageRuleSector(this.bookingData, this.travellerData.xId, this.sectorXId): undefined,
            mealplan: this.bookingData.ssr?.length ? this.sectorAirServicesService.getMealplanServices(getterParameters): undefined,
            payableService: undefined,
            seat: this.bookingData.ssr?.length ? this.sectorAirServicesService.getSeatServices(getterParameters) : undefined,
            seatNumber: this.bookingData.seatAssignment?.length ? getSeatNumber(this.bookingData, this.travellerData.xId, this.sectorXId): undefined,
            ticket: this.bookingData.ssr?.length ? this.sectorAirServicesService.getTicketServices(getterParameters) : undefined,
            ticketNumber: this.bookingData.ticket?.length ? getTicketNumber(this.bookingData, this.travellerData.xId, this.sectorXId): undefined,
        };
    }
}

function getTicketNumber(bookingData: IGetBookingsAirResponse, travellerXId: string, sectorXId: string): string {
    const ticket: ITicket = bookingData.ticket && bookingData.ticket.length ?
        bookingData.ticket.filter(
            (ticketItem: ITicket) =>
                ticketItem.sector &&
                ticketItem.sector.find((ticketSector: IXId) => ticketSector.xId === sectorXId) &&
                ticketItem.traveller &&
                ticketItem.traveller.xId === travellerXId
        )[0] : undefined;
    return ticket && ticket.number ? ticket.number : undefined;
}

function getSeatNumber(bookingData: IGetBookingsAirResponse, travellerXId: string, sectorXId: string): string {
    const seat: ISeatAssignment = bookingData.seatAssignment && bookingData.seatAssignment.length ?
        bookingData.seatAssignment.filter(
            (seatItem: ISeatAssignment) =>
                seatItem.sector &&
                seatItem.sector.xId === sectorXId &&
                seatItem.traveller &&
                seatItem.traveller.xId === travellerXId
        )[0] : undefined;
    return seat && seat.number ? seat.number : undefined;
}

function getLuggageRuleSector(bookingData: IGetBookingsAirResponse, travellerXId: string, sectorXId: string): IRule[] {
    return bookingData.rule.filter(
        (ruleItem: IRule) =>
            ruleItem.xType === "luggage" &&
            ruleItem.sector &&
            ruleItem.sector.find((ruleSector: IXId) => ruleSector.xId === sectorXId) &&
            ruleItem.traveller &&
            ruleItem.traveller.find((ruleTraveller: IXId) => ruleTraveller.xId === travellerXId)
    );
}
